import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Riquadro, Spunta } from '../componenti/base.tsx'
import { IconaIndietro, IconaScarica } from '../componenti/Icone.tsx'
import { FoglioModulo } from '../componenti/FoglioModulo.tsx'
import { giornoItaliano } from '../servizi/regole-consensi.ts'
import type { DueModuli, ModuloCompilato, PdfModulo } from '../servizi/modulo-progetto.ts'
import { FoglioSchedaUtente } from './StampaScheda.tsx'

/**
 * Stampa del modulo del destinatario di un progetto, compilato con i dati della scheda (492).
 *
 * Il foglio lo compone l'API (GET /persone/:id/moduli-progetto/:progetto) con la lettura della scheda e
 * gli incarichi di chi stampa: i dati che la scheda non ha, o che chi stampa non vede, restano righe da
 * scrivere a mano; la riga «Note» e' sempre vuota. L'apertura della pagina e' registrata come
 * `progetto.modulo_stampato`.
 *
 * Due uscite, come la stampa della scheda (StampaScheda.tsx): la stampa del browser e il PDF/A che il
 * Job dei lavori prepara, sigilla e tiene sette giorni. Il modulo firmato si carica dalla scheda,
 * sezione «Moduli dei progetti».
 *
 * «Stampa i due moduli» (`dueModuli`, 495, decisione di Davide del 31/10/2026): la stessa pagina con,
 * dopo il modulo del progetto, il modulo CIDIS dei consensi, cioe' la scheda utente da firmare con il
 * progetto in intestazione e nella fascia il logo CIDIS e i loghi del progetto. Una stampa sola e un
 * PDF/A solo: i due moduli si firmano e si caricano separati. L'unica scelta e' la riga «Nome e
 * qualità del firmatario» del modulo CIDIS, come nella stampa della scheda; per scegliere altri loghi
 * il modulo CIDIS si stampa da solo. L'apertura e' registrata come le due stampe.
 */

/** Attesa fra due richieste dell'elenco dei PDF mentre uno e' in preparazione, e per quanto al massimo. */
const OGNI_MS = 15_000
const PER_MS = 20 * 60_000

const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message
  : 'Non è andata a buon fine: controlla la connessione e riprova.')

const momento = (valore: string | null) => (valore
  ? new Date(valore).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '')

export function StampaModuloProgetto({ dueModuli = false }: { dueModuli?: boolean }) {
  const { id = '', progetto = '' } = useParams()
  const [dati, setDati] = useState<ModuloCompilato | DueModuli | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [pdf, setPdf] = useState<PdfModulo[] | null>(null)
  const [errorePdf, setErrorePdf] = useState<string | null>(null)
  const [richiesta, setRichiesta] = useState(false)
  const [esitoPdf, setEsitoPdf] = useState<string | null>(null)
  // Riga «Nome e qualità del firmatario» del modulo CIDIS, nella stampa dei due moduli.
  const [compila, setCompila] = useState(true)
  const fogli = useRef<HTMLDivElement>(null)
  const inizioAttesa = useRef<number | null>(null)

  useEffect(() => {
    let vivo = true
    api.get<ModuloCompilato | DueModuli>(`/persone/${id}/moduli-progetto/${progetto}${dueModuli ? '/due-moduli' : ''}`)
      .then((d) => { if (vivo) setDati(d) })
      .catch((e) => {
        if (!vivo) return
        setErrore(e instanceof ErroreApi && e.stato === 404
          ? 'Il modulo non si stampa con gli incarichi in corso: serve la lettura in chiaro della scheda, con il codice fiscale.'
          : messaggio(e))
      })
    return () => { vivo = false }
  }, [id, progetto, dueModuli])

  const caricaPdf = useCallback(() => {
    api.get<PdfModulo[]>(`/persone/${id}/moduli-progetto/${progetto}/pdf`)
      .then((r) => { setPdf(r); setErrorePdf(null) })
      .catch((e) => setErrorePdf(messaggio(e)))
  }, [id, progetto])
  useEffect(caricaPdf, [caricaPdf])

  // Mentre un PDF e' in preparazione l'elenco si aggiorna da solo, per venti minuti al massimo.
  const inPreparazione = !!pdf?.some((p) => p.stato === 'in_preparazione')
  useEffect(() => {
    if (!inPreparazione) { inizioAttesa.current = null; return }
    inizioAttesa.current ??= Date.now()
    if (Date.now() - inizioAttesa.current > PER_MS) return
    const t = setTimeout(caricaPdf, OGNI_MS)
    return () => clearTimeout(t)
  }, [inPreparazione, pdf, caricaPdf])

  if (errore) {
    return (
      <>
        <Avviso tipo="errore">{errore}</Avviso>
        <Link className="bottone bottone-secondario" to={`/persone/${id}`}><IconaIndietro /> Torna alla scheda</Link>
      </>
    )
  }
  if (!dati) return <Attesa testo={dueModuli ? 'Preparazione dei due moduli…' : 'Preparazione del modulo…'} />
  const cidis = 'cidis' in dati ? dati.cidis : null

  /** La stampa parte quando i loghi dei fogli sono pronti: altrimenti i fogli escono senza. */
  async function stampa() {
    const immagini = [...(fogli.current?.querySelectorAll('img') ?? [])]
    await Promise.all(immagini.map((i) => i.decode().catch(() => undefined)))
    window.print()
  }

  async function chiediPdf() {
    setRichiesta(true); setErrorePdf(null); setEsitoPdf(null)
    try {
      await api.post(`/persone/${id}/moduli-progetto/${progetto}/pdf`,
        cidis ? { con_cidis: true, firmatario: compila } : undefined)
      setEsitoPdf('Il PDF è in preparazione. Compare qui sotto quando è pronto e ti arriva una notifica; '
        + 'di solito ci vuole qualche minuto.')
      caricaPdf()
    } catch (e) { setErrorePdf(messaggio(e)) }
    finally { setRichiesta(false) }
  }

  async function scaricaPdf(p: PdfModulo) {
    setErrorePdf(null)
    try {
      const { url } = await api.get<{ url: string }>(`/persone/${id}/moduli-progetto/${progetto}/pdf/${p.id}/scarica`)
      window.open(url, '_blank', 'noopener')
    } catch (e) { setErrorePdf(messaggio(e)); caricaPdf() }
  }

  return (
    <>
      <div className="persona-non-stampa">
        <Intestazione
          titolo={cidis ? `Due moduli da firmare · ${dati.progetto.nome_breve} e CIDIS`
            : `Modulo del destinatario · ${dati.progetto.nome_breve}`}
          sottotitolo={<span className="mono">scheda n. {dati.numero_scheda} · {dati.etichetta}</span>}
          azioni={(
            <>
              <Link className="bottone bottone-secondario" to={`/persone/${id}`}><IconaIndietro /> Torna alla scheda</Link>
              <Bottone variante="secondario" icona={<IconaScarica />} inCorso={richiesta} onClick={() => void chiediPdf()}>
                Scarica PDF
              </Bottone>
              <Bottone variante="primario" onClick={() => void stampa()}>Stampa</Bottone>
            </>
          )}
        />
        {cidis
          ? (
            <p className="persona-nota-sezione">
              Il modulo del progetto «{dati.progetto.titolo}» e, su un foglio nuovo, il modulo CIDIS dei consensi, tutti e
              due compilati con i dati della scheda: la persona li firma tutti e due. Le caselle dei consensi restano vuote,
              le barra la persona. Firmati, si caricano separati dalla scheda, sezione «Moduli dei progetti». L’apertura di
              questa pagina è registrata nella cronologia della scheda.
            </p>
            )
          : (
            <p className="persona-nota-sezione">
              Il modulo del progetto «{dati.progetto.titolo}», compilato con i dati della scheda. Quello che la scheda non
              ha resta da scrivere a mano; la riga «Note» è sempre vuota. Firmato, si carica dalla scheda, sezione «Moduli
              dei progetti». L’apertura di questa pagina è registrata nella cronologia della scheda.
            </p>
            )}

        {cidis && (
          <Riquadro titolo="Modulo CIDIS">
            {cidis.riga_firmatario
              ? (
                <Spunta etichetta={<>Compila la riga «Nome e qualità del firmatario» con <strong>{cidis.riga_firmatario}</strong></>}
                        valore={compila} cambia={setCompila}
                        aiuto={`È chi ha firmato i consensi l’ultima volta${cidis.firmatario?.data
                          ? `, il ${giornoItaliano(cidis.firmatario.data)}` : ''}. Se questa volta firma un’altra persona, `
                          + 'togli la spunta: la riga resta da compilare a mano.'} />
                )
              : (
                <p className="persona-nota-sezione">
                  La riga «Nome e qualità del firmatario» resta da compilare a mano: si scrive quando firma un genitore, il
                  tutore, l’amministratore di sostegno o il responsabile della struttura di accoglienza.
                </p>
                )}
            <p className="tenue piccolo">
              In intestazione il progetto {dati.progetto.nome_breve}; nella fascia il logo CIDIS e i loghi del progetto. Per
              scegliere altri loghi il modulo CIDIS si stampa da solo, con «Stampa la scheda».
            </p>
          </Riquadro>
        )}

        <Riquadro titolo="PDF/A generati dal sistema">
          <p className="persona-nota-sezione">
            {cidis
              ? '«Scarica PDF» prepara un PDF/A solo con i due moduli compilati, uno dopo l’altro, ciascuno con la sua '
                + 'numerazione. '
              : '«Scarica PDF» prepara un PDF/A del modulo compilato. '}
            Lo prepara il sistema in qualche minuto e ne registra l’impronta nel registro sigillato; resta disponibile per
            sette giorni e lo scarica solo chi l’ha chiesto.
          </p>
          {esitoPdf && <Avviso tipo="successo">{esitoPdf}</Avviso>}
          {errorePdf && <Avviso tipo="errore">{errorePdf}</Avviso>}
          {pdf === null ? <Attesa /> : !pdf.length ? <p className="tenue piccolo">Nessun PDF richiesto per questo modulo.</p> : (
            <ul className="stampa-pdf-elenco">
              {pdf.map((p) => (
                <li key={p.id}>
                  <span>
                    {momento(p.richiesto_il)}
                    {p.due_moduli ? ' · con il modulo CIDIS' : ''}
                    {p.pagine ? ` · ${p.pagine} ${p.pagine === 1 ? 'pagina' : 'pagine'}` : ''}
                  </span>
                  {p.stato === 'pronto' && (
                    <Bottone variante="secondario" dimensione="piccolo" icona={<IconaScarica />} onClick={() => void scaricaPdf(p)}>
                      Scarica
                    </Bottone>
                  )}
                  {p.stato === 'in_preparazione' && <span className="tenue">In preparazione…</span>}
                  {p.stato === 'scaduto' && <span className="tenue">Non più disponibile</span>}
                  {p.stato === 'non_riuscito' && <span className="tenue">Non riuscito: chiedine uno nuovo</span>}
                </li>
              ))}
            </ul>
          )}
        </Riquadro>
      </div>

      <div ref={fogli} className={cidis ? 'due-moduli' : undefined}>
        <FoglioModulo foglio={dati.foglio} loghi={dati.loghi}
                      etichetta={`Modulo del destinatario del progetto ${dati.progetto.nome_breve}`} />
        {cidis && (
          <FoglioSchedaUtente foglio={cidis.foglio} progetto={cidis.foglio.testata.progetto}
                              riga={compila ? cidis.riga_firmatario : null} loghi={cidis.loghi} />
        )}
      </div>
    </>
  )
}
